import React, { useState, useEffect, useCallback, useMemo } from "react";

import "../../../../dist/assets/stylesheets/css/reviewPatient.css";
import { getValuesMetrics } from "../../../util/metricsAPI";
import {
  normalizeAnglesLikeSeries,
  resolveSampleRateHz,
  shouldLogAngleSeriesInstrumentation,
} from "../../../util/metricSeriesNormalize";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { updateCurrentTime } from "../../../store/video-slice";
import { chartActions } from "../../../store/chart-slice";
import { formatSensorMetricLabel } from "../../../util/sensorMetrics";
import {
  buildContactMarkers,
  chartContactKey,
  metricSideFromName,
} from "../../../util/floorContacts";
import MetricTimeSeriesChart from "./MetricTimeSeriesChart";
import {
  shiftLinePoints,
  buildLinePointsFromMetricSeries,
} from "./metricTimeSeriesChartUtils";

function DisplayCharts(props) {
  const {
    trial,
    metricName,
    metricType = "angles",
    axisYTitle = "Degrees",
    sensorOffset = 0,
    videoDuration,
    floorContacts = null,
    contactsAvailable = false,
    sampleRateHz,
    label,
    stepped = false,
  } = props;
  const dispatch = useDispatch();
  const displayMetricName =
    label ?? (metricType === "sensors" ? formatSensorMetricLabel(metricName) : metricName);
  const [metricSeries, setMetricSeries] = useState(null);

  const chartKey = chartContactKey(metricType, metricName);
  const contactGlobalVisible = useSelector(
    (state) => state.chart.contactGlobalVisible
  );
  const contactTypeVisible = useSelector(
    (state) => state.chart.contactTypeVisible
  );
  const contactSourceVisible = useSelector(
    (state) => state.chart.contactSourceVisible
  );
  const contactChartVisibleMap = useSelector(
    (state) => state.chart.contactChartVisible
  );
  const chartContactsVisible = contactChartVisibleMap?.[chartKey] !== false;

  // The compare pages drive the playhead through props off their own slices. The
  // review page leaves it out so only the charts re-render as the video plays,
  // instead of the whole page including the drag-and-drop list.
  const storeCurrentTime = useSelector((state) => state.video.currentTime);
  const currentTime = props.currentTime ?? storeCurrentTime ?? 0;

  const handleSeek = useCallback(
    (time) => {
      if (props.videoRef.current) {
        props.videoRef.current.currentTime = time;
      }
      props.setCurrentTime?.(time);
      dispatch(updateCurrentTime(time));
    },
    [dispatch, props.setCurrentTime, props.videoRef]
  );

  // Scrubbing calls onSeek for every pointer move. Placement is a separate
  // click so a drag follows the video without dropping a mark at each sample.
  const handleAnnotateClick = useCallback(
    (time) => {
      if (props.annotateMode && typeof props.onPlaceAnnotation === 'function') {
        props.onPlaceAnnotation(time, metricName);
      }
    },
    [metricName, props.annotateMode, props.onPlaceAnnotation]
  );

  useEffect(() => {
    getValuesMetrics(trial, metricType, metricName).then((res) => {
      if (!res) {
        setMetricSeries(null);
        return;
      }
      const resNorm =
        metricType === "angles"
          ? normalizeAnglesLikeSeries(res, {
              trialId: trial,
              metricName,
              logDebug: shouldLogAngleSeriesInstrumentation(trial),
            })
          : res;
      setMetricSeries(resNorm);
    });
  }, [trial, metricType, metricName]);

  const rawLinePoints = useMemo(
    () =>
      buildLinePointsFromMetricSeries(
        metricSeries,
        videoDuration,
        props.videoRef,
        resolveSampleRateHz,
        sampleRateHz
      ),
    [metricSeries, videoDuration, props.videoRef, sampleRateHz]
  );

  // Residual slider only. Device sync is already in the served series, so a
  // stored handshake is not applied here. Slider 0 leaves x on video time.
  const plotOffset = metricType === "sensors" ? sensorOffset : 0;

  const linePoints = useMemo(() => {
    return shiftLinePoints(rawLinePoints, plotOffset);
  }, [rawLinePoints, plotOffset]);

  // X-axis stays on the video timeline; only the sensor series shifts under a
  // fixed playhead at currentTime (rescale-to-shifted-extents panned the grid).
  const axisExtents = useMemo(() => {
    if (!rawLinePoints.length) return { minX: 0, maxX: 0 };
    return {
      minX: rawLinePoints[0].x,
      maxX: rawLinePoints[rawLinePoints.length - 1].x,
    };
  }, [rawLinePoints]);

  const contactMarkers = useMemo(() => {
    if (!contactsAvailable || !floorContacts?.length) return [];
    // Contacts come from the cameras, so they are already on video time. The
    // sensor offset moves the series onto that clock; shifting the contacts too
    // would cancel it and drop them a fraction of a stride off (reads as the
    // other foot). Side-specific charts keep that side only.
    return buildContactMarkers(linePoints, floorContacts, {
      globalVisible: contactGlobalVisible,
      typeVisible: contactTypeVisible,
      sourceVisible: contactSourceVisible,
      chartVisible: chartContactsVisible,
      side: metricSideFromName(metricName),
    });
  }, [
    contactsAvailable,
    floorContacts,
    linePoints,
    contactGlobalVisible,
    contactTypeVisible,
    contactSourceVisible,
    chartContactsVisible,
    metricName,
  ]);

  const handleChartContactsToggle = (e) => {
    dispatch(
      chartActions.setContactChartVisible({
        chartKey,
        visible: e.target.checked,
      })
    );
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <figure>
        <div className="graph-title">
          <span className="drag-container" {...props.providedDragHandleProps}>
            <Icon icon="akar-icons:drag-vertical" />
          </span>
          {displayMetricName}
          {contactsAvailable ? (
            <label
              className="chart-contacts-toggle"
              data-testid={`chart-contacts-toggle-${chartKey}`}
            >
              <input
                type="checkbox"
                checked={chartContactsVisible}
                onChange={handleChartContactsToggle}
                aria-label={`Show contact markers on ${displayMetricName}`}
              />
              <span>Contacts</span>
            </label>
          ) : null}
          <button
            type="button"
            className="chart-close-btn"
            onClick={() => props.deleteChart()}
            aria-label="Delete chart"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>
        {trial ? (
          <div className="review-chart-body">
            <MetricTimeSeriesChart
              linePoints={linePoints}
              playheadTime={currentTime}
              seriesMinX={axisExtents.minX}
              seriesMaxX={axisExtents.maxX}
              axisYTitle={axisYTitle}
              onSeek={handleSeek}
              onAnnotateClick={handleAnnotateClick}
              inComparePage={props.inComparePage}
              height={props.chartHeight}
              contactMarkers={contactMarkers}
              annotateMode={props.annotateMode}
              annotateType={props.annotateType}
              onRemoveAnnotation={props.onRemoveAnnotation}
              stepped={stepped}
            />
          </div>
        ) : null}
      </figure>
    </div>
  );
}

export default DisplayCharts;
